import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  ArrowRight,
  Globe,
  Search,
  Sparkles,
  Send,
  CheckCircle2,
  Calendar,
  MessageCircle,
  Heart,
  Repeat2,
  Shield,
  Bot,
} from "lucide-react";
import SEO, {
  faqSchema,
  ORGANIZATION_SCHEMA,
  SITE_URL,
} from "../components/SEO";
import MarketingNav from "../components/marketing/MarketingNav";
import MarketingFooter from "../components/marketing/MarketingFooter";
import GIF_IMAGE from "../assets/gifs/export-1787103832409.gif";
import HEADSHOT_JUSTIN from "../assets/photos/justin_headshot.jpg";
import HEADSHOT_VAISHNAVI from "../assets/photos/vaishnavi_headshot.png";
import HEADSHOT_THIRD from "../assets/photos/third_headshot.jpg";
import HEADSHOT_FOURTH from "../assets/photos/fourth_headshot.jpg";
import BookDemo from "../components/marketing/BookDemo";
import {
  buildLeadSignupPath,
  normalizeWebsiteUrl,
} from "../utils/leadOnboarding";

const SOCIAL_PROOF_MEMBERS = 2000;
const SOCIAL_PROOF_AVATARS = [
  HEADSHOT_JUSTIN,
  HEADSHOT_VAISHNAVI,
  HEADSHOT_THIRD,
  HEADSHOT_FOURTH,
];

function SocialProof() {
  const remaining = (
    SOCIAL_PROOF_MEMBERS - SOCIAL_PROOF_AVATARS.length
  ).toLocaleString("en-US");

  return (
    <div className="mt-6 sm:mt-7 flex flex-col items-center gap-3">
      <p className="text-xs sm:text-sm text-[#5a6478]">
        Join{" "}
        <span className="font-semibold text-[#0a1736]">
          {SOCIAL_PROOF_MEMBERS.toLocaleString("en-US")}+
        </span>{" "}
        businesses and freelancers
      </p>

      <div className="flex items-center -space-x-2.5 sm:-space-x-3">
        {SOCIAL_PROOF_AVATARS.map((src, i) => (
          <img
            key={src}
            src={src}
            alt=""
            aria-hidden="true"
            width="44"
            height="44"
            className="relative h-9 w-9 sm:h-11 sm:w-11 shrink-0 rounded-full object-cover bg-[#f3ecdd] ring-2 ring-[#faf6ee] shadow-[0_1px_3px_rgba(10,23,54,0.18)]"
            style={{ zIndex: SOCIAL_PROOF_AVATARS.length - i }}
          />
        ))}
        <span className="inline-flex h-9 sm:h-11 shrink-0 items-center justify-center rounded-full bg-[#0a1736] pl-3.5 pr-2.5 sm:pl-5 sm:pr-3 text-[11px] sm:text-xs font-medium text-white ring-2 ring-[#faf6ee] shadow-[0_1px_3px_rgba(10,23,54,0.18)]">
          +{remaining}
        </span>
      </div>
    </div>
  );
}

function Hero() {
  const navigate = useNavigate();
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [websiteError, setWebsiteError] = useState("");

  const startLeadSearch = (event) => {
    event.preventDefault();
    try {
      const normalizedUrl = normalizeWebsiteUrl(websiteUrl);
      setWebsiteError("");
      navigate(buildLeadSignupPath(normalizedUrl));
    } catch (error) {
      setWebsiteError(error.message);
    }
  };

  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 -z-10 bg-[#faf6ee]" />
      <div
        className="absolute inset-0 -z-10 opacity-70"
        style={{
          backgroundImage:
            "radial-gradient(900px 420px at 50% -12%, rgba(45,91,255,0.13), transparent 65%)",
        }}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 lg:pt-28 pb-16 sm:pb-20 lg:pb-24">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: "easeOut" }}
          className="max-w-4xl xl:max-w-5xl mx-auto text-center"
        >
          <h1 className="font-display text-[#0a1736] text-[2rem] sm:text-5xl md:text-6xl lg:text-[4.25rem] font-semibold leading-[1.07] tracking-[-0.02em] text-balance">
            Find your Leads.{" "}
            <span className="bg-gradient-to-r from-[#2d5bff] via-[#7b6bf0] to-[#f46a4e] bg-clip-text text-transparent">
              Then, reply like a human.
            </span>
          </h1>

          <p className="mt-5 sm:mt-6 text-base sm:text-lg lg:text-xl text-[#5a6478] max-w-3xl mx-auto leading-relaxed">
            Find leads on X (Twitter), LinkedIn, and Reddit{" "}
            <mark className="bg-[#fff3a3] text-[#0a1736] px-1 rounded-sm">
              through high-intent posts.
            </mark>
            Collavue targets high-intent conversations,{" "}
            <mark className="bg-[#fff3a3] text-[#0a1736] px-1 rounded-sm">
              scores them based on if your service is solving the problem
            </mark>
            , and{" "}
            <mark className="bg-[#fff3a3] text-[#0a1736] px-1 rounded-sm">
              drafts an on-brand reply for your approval
            </mark>{" "}
            so you reach{" "}
            <mark className="bg-[#fff3a3] text-[#0a1736] px-1 rounded-sm">
              the ideal buyer
            </mark>{" "}
            instead of duds.
          </p>

          <form
            id="find-leads"
            onSubmit={startLeadSearch}
            className="mt-8 sm:mt-9 max-w-2xl mx-auto"
          >
            <div className="flex flex-col sm:flex-row items-stretch gap-3">
              <label htmlFor="lead-website" className="sr-only">
                Website URL
              </label>
              <div className="relative flex-1">
                <Globe
                  aria-hidden="true"
                  className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5a6478]"
                />
                <input
                  id="lead-website"
                  type="text"
                  inputMode="url"
                  autoComplete="url"
                  value={websiteUrl}
                  onChange={(event) => {
                    setWebsiteUrl(event.target.value);
                    if (websiteError) setWebsiteError("");
                  }}
                  placeholder="Enter your website URL"
                  aria-describedby={websiteError ? "lead-website-error" : undefined}
                  aria-invalid={Boolean(websiteError)}
                  className="w-full rounded-lg border border-[#d5cdbd] bg-white py-3.5 pl-12 pr-4 text-[#0a1736] shadow-sm outline-none transition focus:border-[#2d5bff] focus:ring-2 focus:ring-[#2d5bff]/20"
                />
              </div>
              <button
                type="submit"
                className="inline-flex shrink-0 items-center justify-center rounded-lg bg-[#0a1736] px-7 py-3.5 font-medium text-white transition-colors hover:bg-[#1c2a4d]"
              >
                Find My First 5 Leads →
              </button>
            </div>
            {websiteError && (
              <p id="lead-website-error" role="alert" className="mt-2 text-sm text-red-700">
                {websiteError}
              </p>
            )}
          </form>

          <div className="mt-3 flex justify-center">
            <a
              href="#book-demo"
              className="inline-flex items-center justify-center text-sm font-medium text-[#0a1736] underline decoration-[#0a1736]/30 underline-offset-4 hover:decoration-[#0a1736]"
            >
              Book a Demo
            </a>
          </div>

          <p className="mt-4 text-xs sm:text-sm text-[#5a6478] max-w-2xl mx-auto leading-relaxed">
            Collavue analyzes your site and searches Reddit, X, and LinkedIn.
            See 5 real potential leads before you pay.
          </p>
          <SocialProof />
        </motion.div>

        {/* The product loop gets the full column width now — it is the proof,
            so it no longer shares the fold with a text column. */}
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15, ease: "easeOut" }}
          className="mt-12 sm:mt-14 lg:mt-16 max-w-6xl mx-auto"
        >
          <HeroProductDemo />
        </motion.div>

        <TrustedBy />
      </div>
    </section>
  );
}

function HeroProductDemo() {
  return (
    <figure className="relative">
      <div className="pointer-events-none absolute -inset-4 sm:-inset-6 -z-10 rounded-[32px] bg-[#2d5bff]/10 blur-3xl" />

      <div className="rounded-xl sm:rounded-2xl border border-[#e3dccb] bg-white shadow-[0_40px_90px_-45px_rgba(10,23,54,0.5)] overflow-hidden">
        {/* Aspect-ratio box: reserves the exact 16:9 slot before the GIF
            arrives, so the trusted-by strip below never jumps. */}
        <div className="relative w-full aspect-[16/9] bg-[#f3ecdd]">
          <img
            src={GIF_IMAGE}
            alt="Collavue scanning X, LinkedIn, and Reddit for high-intent posts, scoring them, and drafting a reply for approval"
            width={1920}
            height={1080}
            loading="eager"
            decoding="async"
            className="absolute inset-0 w-full h-full object-contain"
          />
        </div>
      </div>

      <figcaption className="mt-3 text-center text-xs sm:text-sm text-[#5a6478] px-4">
        Real product: discovery, intent scoring, and drafted replies — no autoposting.
      </figcaption>
    </figure>
  );
}

function TrustedBy() {
  const logos = [
    {
      label: "Zenstream",
      url: "https://zenstream.app",
      favicon: "https://www.google.com/s2/favicons?domain=zenstream.app&sz=64",
    },
    {
      label: "GentleGourmet",
      url: "https://gentlegourmet.app",
      favicon: "https://www.google.com/s2/favicons?domain=gentlegourmet.app&sz=64",
    },
  ];

  return (
    <div className="mt-12 sm:mt-14 lg:mt-16">
      <p className="text-center text-[11px] sm:text-xs uppercase tracking-[0.18em] text-[#5a6478]">
        Used by the fastest growing SaaS teams in the world
      </p>

      <div className="mt-6 flex flex-wrap items-center justify-center gap-x-8 sm:gap-x-14 gap-y-5">
        {logos.map((logo) => (
          <a
            key={logo.label}
            href={logo.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-2.5 text-[#0a1736]/60 hover:text-[#0a1736] transition-colors"
          >
            <img
              src={logo.favicon}
              alt=""
              aria-hidden="true"
              width="24"
              height="24"
              loading="lazy"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              className="h-5 w-5 sm:h-6 sm:w-6 rounded-sm opacity-70 group-hover:opacity-100 transition-opacity"
            />
            <span className="font-display text-lg sm:text-xl tracking-tight">
              {logo.label}
            </span>
          </a>
        ))}
      </div>

      <div className="mt-7 text-center">
        <a
          href="#case-studies"
          className="inline-flex items-center gap-1 text-sm text-[#0a1736] underline decoration-[#0a1736]/30 underline-offset-4 hover:decoration-[#0a1736]"
        >
          How Zenstream grew their enterprise clientele with big-ticket names
          <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
}

function CaseStudyAnimation() {
  const events = [
    {
      time: "00:00",
      kind: "scan",
      title: "Scanning X (Twitter), LinkedIn & Reddit",
      detail: 'Query: "video moderation" OR "ban appeal" OR "false positive"',
    },
    {
      time: "00:02",
      kind: "match",
      title: "Match found · @priya.builds",
      detail: "Complaining about over-aggressive bans on Youtube.",
    },
    {
      time: "00:03",
      kind: "score",
      title: "Intent score: 94 / 100",
      detail: "Pain matches Zenstream ICP. Risk of churn from current vendor: high.",
    },
    {
      time: "00:05",
      kind: "draft",
      title: "Drafting reply in Zenstream voice",
      detail:
        "Acknowledges the over-moderation problem, links to a 2-min demo, asks one question.",
    },
    {
      time: "00:06",
      kind: "approve",
      title: "Sent to human reviewer",
      detail: "Approved in 11s. DM scheduled. Demo booked 4h later.",
    },
  ];

  const [active, setActive] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setActive((a) => (a + 1) % events.length), 2200);
    return () => clearInterval(id);
  }, [events.length]);

  return (
    <section id="case-studies" className="relative bg-[#faf6ee]">
      <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28">
        <div className="max-w-3xl">
          <p className="text-[#2d5bff] text-sm uppercase tracking-wider font-medium">
            Case study · live replay
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-[#0a1736] font-semibold mt-3 leading-tight">
            How <span className="italic">Zenstream</span> turned a Twitter complaint into a
            booked demo in <span className="text-[#2d5bff]">4 hours</span>.
          </h2>
          <p className="mt-4 text-[#5a6478] text-lg">
            Zenstream is an enterprise SaaS that moderates videos on OBS streams. Their #1 lead source used to be
            paid search. Now it's people venting publicly.
          </p>
        </div>

        <div className="mt-12 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 space-y-3">
            {events.map((e, i) => {
              const isActive = i === active;
              const isPast = i < active;
              return (
                <motion.div
                  key={i}
                  animate={{
                    opacity: isActive ? 1 : isPast ? 0.7 : 0.4,
                    scale: isActive ? 1 : 0.99,
                  }}
                  transition={{ duration: 0.4 }}
                  className={`flex gap-4 p-5 rounded-xl border bg-white ${isActive ? "border-[#2d5bff] shadow-[0_10px_40px_-20px_rgba(45,91,255,0.4)]" : "border-[#e3dccb]"
                    }`}
                >
                  <div className="flex flex-col items-center">
                    <span
                      className={`w-9 h-9 rounded-lg flex items-center justify-center ${isActive
                        ? "bg-[#2d5bff] text-white"
                        : isPast
                          ? "bg-[#0a1736] text-white"
                          : "bg-[#f3ecdd] text-[#1c2a4d]"
                        }`}
                    >
                      {e.kind === "scan" && <Search className="w-4 h-4" />}
                      {e.kind === "match" && <MessageCircle className="w-4 h-4" />}
                      {e.kind === "score" && <Sparkles className="w-4 h-4" />}
                      {e.kind === "draft" && <Bot className="w-4 h-4" />}
                      {e.kind === "approve" && <CheckCircle2 className="w-4 h-4" />}
                    </span>
                    {i < events.length - 1 && (
                      <span className="flex-1 w-px bg-[#e3dccb] mt-2" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display text-lg text-[#0a1736] font-semibold">
                        {e.title}
                      </h3>
                      <span className="text-xs text-[#5a6478] tabular-nums">{e.time}</span>
                    </div>
                    <p className="text-sm text-[#5a6478] mt-1">{e.detail}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>

          <div className="lg:col-span-5 space-y-4">
            <TwitterPost active={active >= 1} />
            <AgentReplyCard active={active >= 3} />
          </div>
        </div>

        <div className="mt-16 grid sm:grid-cols-3 gap-6 border-t border-[#e3dccb] pt-10">
          <Stat number="3" label="Leads Zenstream acquired weekly" />
          <Stat number="38" label="Demos booked from public complaints / mo" />
          <Stat number="11s" label="Avg. human approval time on Collavue drafts" />
        </div>
      </div>
    </section>
  );
}

function Stat({ number, label }) {
  return (
    <div>
      <div className="font-display text-4xl md:text-5xl text-[#0a1736] font-semibold">
        {number}
      </div>
      <div className="text-[#5a6478] mt-2 text-sm">{label}</div>
    </div>
  );
}

function TwitterPost({ active }) {
  return (
    <motion.div
      animate={{ opacity: active ? 1 : 0.5, y: active ? 0 : 4 }}
      transition={{ duration: 0.5 }}
      className="bg-white rounded-xl border border-[#e3dccb] p-5"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#2d5bff] to-[#0a1736]" />
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[#0a1736] text-sm">Priya Mehta</span>
            <span className="text-[#5a6478] text-xs">@priya.builds · 2h</span>
            <span className="ml-auto text-xs text-[#5a6478]">𝕏</span>
          </div>
          <p className="text-[#0a1736] text-sm mt-2 leading-relaxed">
            Why is it so hard to moderate videos in 2026, why isn't there a tool that
            properly moderates videos without dishing out bans unforgivingly? Channel just got demonetized...
          </p>
          <div className="flex items-center gap-6 mt-3 text-[#5a6478] text-xs">
            <span className="flex items-center gap-1">
              <MessageCircle className="w-3.5 h-3.5" /> 84
            </span>
            <span className="flex items-center gap-1">
              <Repeat2 className="w-3.5 h-3.5" /> 211
            </span>
            <span className="flex items-center gap-1">
              <Heart className="w-3.5 h-3.5" /> 1.4k
            </span>
          </div>
        </div>
      </div>
      <AnimatePresence>
        {active && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-3 pt-3 border-t border-[#e3dccb] flex items-center gap-2 text-xs"
          >
            <Sparkles className="w-3.5 h-3.5 text-[#2d5bff]" />
            <span className="text-[#2d5bff] font-medium">
              Collavue: matched Zenstream ICP · intent 94
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function AgentReplyCard({ active }) {
  return (
    <motion.div
      animate={{ opacity: active ? 1 : 0.3, y: active ? 0 : 8 }}
      transition={{ duration: 0.5 }}
      className="bg-[#0a1736] text-white rounded-xl p-5 border border-[#0a1736]"
    >
      <div className="flex items-center gap-2">
        <Bot className="w-4 h-4 text-[#2d5bff]" />
        <span className="text-xs uppercase tracking-wider text-white/70">
          Drafted by Collavue · for Zenstream
        </span>
      </div>
      <p className="mt-3 text-sm leading-relaxed">
        Hey Priya — saw your thread. We built Zenstream exactly for this. Our model
        scores intent + context (sarcasm, in-jokes, repeat offenders) instead of
        keyword-matching, so power users don't get nuked for edge cases. Happy to send
        a 90-second teardown of how we'd handle your last 3 false positives — interested?
      </p>
      <div className="mt-4 flex items-center justify-between text-xs">
        <span className="text-white/60">Awaiting human review</span>
        <span className="text-[#bef264]">11s avg approval</span>
      </div>
    </motion.div>
  );
}

function ReplyAgentSection() {
  return (
    <section
      id="reply-agent"
      className="relative bg-[#0a1736] text-white overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(800px 300px at 20% 10%, rgba(45,91,255,0.45), transparent 60%), radial-gradient(600px 300px at 90% 90%, rgba(190,242,100,0.18), transparent 60%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <p className="text-[#bef264] text-sm uppercase tracking-wider font-medium">
            Collavue · Reply Agent
          </p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold mt-3 leading-[1.05]">
            Reply Agent That Books Demos and{" "}
            <span className="italic text-[#bef264]">Drives Signups.</span>
          </h2>
          <p className="mt-6 text-lg text-white/75 max-w-xl leading-relaxed">
            Let Collavue outreach to your leads for you and automate the whole process
            from lead discovery to booking demos.
          </p>

          <div className="mt-8 grid sm:grid-cols-2 gap-3 max-w-md">
            <FeatureBadge icon={Search} label="Find" />
            <FeatureBadge icon={Sparkles} label="Score" />
            <FeatureBadge icon={Bot} label="Reply" />
            <FeatureBadge icon={Calendar} label="Book" />
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link
              to="/campaigns/new"
              className="px-6 py-3 rounded-md bg-white text-[#0a1736] font-medium hover:bg-[#faf6ee] transition-colors inline-flex items-center gap-2"
            >
              Try Reply Agent <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="#case-studies"
              className="px-6 py-3 rounded-md border border-white/25 text-white font-medium hover:border-white transition-colors"
            >
              See it in action
            </a>
          </div>
        </div>

        <div className="lg:col-span-6">
          <ReplyAgentFlow />
        </div>
      </div>
    </section>
  );
}

function FeatureBadge({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 border border-white/10">
      <Icon className="w-4 h-4 text-[#bef264]" />
      <span className="text-sm">{label}</span>
    </div>
  );
}

function ReplyAgentFlow() {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1800);
    return () => clearInterval(id);
  }, []);

  const stages = [
    { icon: Search, label: "Discover lead", meta: "Public conversation" },
    { icon: Sparkles, label: "Score intent", meta: "94 / 100" },
    { icon: Bot, label: "Draft reply", meta: "In your voice" },
    { icon: Send, label: "Send", meta: "Human-approved" },
    { icon: Calendar, label: "Demo booked", meta: "Auto-scheduled" },
  ];

  const current = tick % stages.length;

  return (
    <div className="relative bg-white/5 backdrop-blur rounded-2xl border border-white/10 p-6">
      <div className="flex items-center justify-between mb-6">
        <span className="text-xs uppercase tracking-wider text-white/60">
          Pipeline · live
        </span>
        <span className="flex items-center gap-2 text-xs text-[#bef264]">
          <span className="w-2 h-2 rounded-full bg-[#bef264] animate-pulse" />
          Running
        </span>
      </div>

      <div className="space-y-3">
        {stages.map((s, i) => {
          const Icon = s.icon;
          const isActive = i === current;
          return (
            <motion.div
              key={i}
              animate={{
                backgroundColor: isActive ? "rgba(45,91,255,0.18)" : "rgba(255,255,255,0.03)",
                borderColor: isActive ? "rgba(190,242,100,0.6)" : "rgba(255,255,255,0.08)",
              }}
              transition={{ duration: 0.4 }}
              className="flex items-center gap-3 px-4 py-3 rounded-lg border"
            >
              <span
                className={`w-8 h-8 rounded-md flex items-center justify-center ${isActive ? "bg-[#bef264] text-[#0a1736]" : "bg-white/10 text-white"
                  }`}
              >
                <Icon className="w-4 h-4" />
              </span>
              <div className="flex-1">
                <div className="text-sm font-medium">{s.label}</div>
                <div className="text-xs text-white/60">{s.meta}</div>
              </div>
              {isActive && (
                <motion.span
                  initial={{ opacity: 0, x: -4 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="text-xs text-[#bef264]"
                >
                  active
                </motion.span>
              )}
            </motion.div>
          );
        })}
      </div>

      <div className="mt-6 pt-6 border-t border-white/10 grid grid-cols-3 gap-4 text-center">
        <div>
          <div className="font-display text-2xl">128</div>
          <div className="text-xs text-white/60">Leads / wk</div>
        </div>
        <div>
          <div className="font-display text-2xl">42</div>
          <div className="text-xs text-white/60">Replies sent</div>
        </div>
        <div>
          <div className="font-display text-2xl text-[#bef264]">17</div>
          <div className="text-xs text-white/60">Demos booked</div>
        </div>
      </div>
    </div>
  );
}

function HowItWorks() {
  const items = [
    {
      step: "01",
      title: "Add your offer",
      body: "Drop in your website. We auto-extract ICP, keywords, and disqualifiers.",
    },
    {
      step: "02",
      title: "Get a daily email digest",
      body: "We search X, LinkedIn, and Reddit every day and email you the new leads — hot-lead count included. No autoposting.",
    },
    {
      step: "03",
      title: "Ask your leads anything",
      body: '"Find every lead that mentions pricing." Save that search and it runs on every daily sync.',
    },
    {
      step: "04",
      title: "Approve & send",
      body: "Drafts are written in your voice. You ship them in 11 seconds.",
    },
  ];

  return (
    <section id="product" className="bg-white">
      <div className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-3xl">
          <p className="text-[#2d5bff] text-sm uppercase tracking-wider font-medium">
            How it works
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-[#0a1736] font-semibold mt-3 leading-tight">
            From public conversation to{" "}
            <span className="italic text-[#2d5bff]">booked demo</span>, on autopilot.
          </h2>
          <p className="text-[#5a6478] mt-4 text-base leading-relaxed">
            See how Collavue turns real conversations into qualified outreach opportunities.
          </p>
        </div>

        <div className="mt-14 relative overflow-hidden rounded-[28px] border border-[#e3dccb] bg-[#faf6ee] p-6 md:p-8">
          {/* soft backdrop glow */}
          <div className="pointer-events-none absolute -top-20 -right-12 h-64 w-64 rounded-full bg-[#2d5bff]/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-12 h-64 w-64 rounded-full bg-[#ff6b2c]/10 blur-3xl" />

          <div className="relative grid lg:grid-cols-[1.05fr_0.95fr] gap-8 items-start">
            {/* Left side: steps */}
            <div>
              <div className="grid sm:grid-cols-2 gap-5">
                {items.map((it) => (
                  <div
                    key={it.step}
                    className="rounded-2xl border border-[#e3dccb] bg-white/90 backdrop-blur-sm p-6 shadow-[0_10px_30px_rgba(10,23,54,0.04)] hover:bg-white transition-colors"
                  >
                    <span className="font-display text-3xl text-[#2d5bff]">
                      {it.step}
                    </span>
                    <h3 className="font-display text-lg sm:text-xl text-[#0a1736] font-semibold mt-3 break-words">
                      {it.title}
                    </h3>
                    <p className="text-[#5a6478] text-sm mt-2 leading-relaxed">
                      {it.body}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Right side: X (Twitter) style visual */}
            <div className="relative">
              <div className="rounded-3xl border border-[#d7dbe4] bg-white shadow-[0_20px_60px_rgba(10,23,54,0.10)] overflow-hidden">
                {/* top bar */}
                <div className="flex items-center justify-between border-b border-[#edf0f4] px-5 py-4 bg-[#f8f9fb]">
                  <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-[#0a1736] flex items-center justify-center text-white text-sm font-bold">
                      𝕏
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#0a1736]">
                        @growthops_founder
                      </p>
                      <p className="text-xs text-[#758198]">
                        Posted on X · 3h ago
                      </p>
                    </div>
                  </div>
                  <span className="rounded-full bg-[#eef3ff] px-3 py-1 text-xs font-medium text-[#2d5bff]">
                    High intent
                  </span>
                </div>

                {/* body */}
                <div className="p-5">
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[#2d5bff] font-semibold">
                    Mock X post
                  </p>

                  <h3 className="mt-3 text-[1.05rem] md:text-[1.15rem] font-semibold text-[#0a1736] leading-snug">
                    What tool are people using to find X posts where users are
                    actively asking for a solution?
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-[#4f5d75]">
                    I run a B2B SaaS and want to find posts where people are clearly
                    describing a pain point, like{" "}
                    <span className="font-medium text-[#0a1736]">
                      “we’re losing time manually qualifying leads”
                    </span>{" "}
                    or{" "}
                    <span className="font-medium text-[#0a1736]">
                      “is there a tool that finds high intent conversations on X?”
                    </span>
                    . Ideally I want something that scores intent and drafts a reply,
                    but still lets me approve it manually.
                  </p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="rounded-full bg-[#f4f6fa] px-3 py-1 text-xs text-[#5a6478]">
                      Lead generation
                    </span>
                    <span className="rounded-full bg-[#f4f6fa] px-3 py-1 text-xs text-[#5a6478]">
                      X / Twitter
                    </span>
                    <span className="rounded-full bg-[#f4f6fa] px-3 py-1 text-xs text-[#5a6478]">
                      LinkedIn
                    </span>
                    <span className="rounded-full bg-[#f4f6fa] px-3 py-1 text-xs text-[#5a6478]">
                      Reddit
                    </span>
                    <span className="rounded-full bg-[#f4f6fa] px-3 py-1 text-xs text-[#5a6478]">
                      Outreach automation
                    </span>
                  </div>

                  {/* actions */}
                  <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-[#758198] border-t border-[#edf0f4] pt-4">
                    <span>♥ 184</span>
                    <span>56 replies</span>
                    <span>211 reposts</span>
                    <span>Share</span>
                  </div>
                </div>

                {/* Collavue analysis box */}
                <div className="border-t border-[#edf0f4] bg-[#fbfcfe] p-5">
                  <div className="rounded-2xl border border-[#dbe4ff] bg-[#f5f8ff] p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <p className="text-sm font-semibold text-[#0a1736]">
                          Collavue detected a hot lead
                        </p>
                        <p className="text-xs text-[#5a6478] mt-1">
                          Intent score: 92/100 · Risk: Low · Recommended action: Reply
                        </p>
                      </div>
                      <span className="rounded-full bg-[#2d5bff] px-3 py-1 text-xs font-medium text-white">
                        Ready to draft
                      </span>
                    </div>

                    <div className="mt-4 rounded-xl border border-[#d9e3ff] bg-white p-4">
                      <p className="text-xs uppercase tracking-wider text-[#2d5bff] font-medium">
                        Suggested reply
                      </p>
                      <p className="mt-2 text-sm leading-7 text-[#445066]">
                        If you want, I’m building a tool that does exactly this — it
                        scans X, LinkedIn, and Reddit for high-intent posts, scores them, and
                        drafts replies in your brand voice for approval. Happy to share
                        a quick demo if helpful.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* floating mini stat card */}
              <div className="hidden md:block absolute -bottom-5 -left-5 rounded-2xl border border-[#e3dccb] bg-white px-4 py-3 shadow-[0_12px_30px_rgba(10,23,54,0.10)]">
                <p className="text-xs uppercase tracking-wider text-[#5a6478]">
                  surfaced automatically
                </p>
                <p className="mt-1 font-display text-2xl text-[#0a1736] font-semibold">
                  Top 5%
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}



function ThreeSimpleSteps() {
  const steps = [
    {
      title: "Create a Campaign",
      body: "Tell us what you offer and who you want to reach.",
      eyebrow: "ICP + offer",
    },
    {
      title: "Add keywords",
      body: "Enter the pain points and buying signals your customers use.",
      eyebrow: "Pain points + intent",
    },
    {
      title: "We scan the internet so you don't have to",
      body: "Collavue finds matching people and delivers qualified leads.",
      eyebrow: "Matching conversations",
    },
  ];

  const sources = ["X", "Twitter"];
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setActive((v) => (v + 1) % steps.length);
    }, 2200);

    return () => clearInterval(id);
  }, [steps.length]);

  return (
    <section
      className="relative overflow-hidden bg-white border-t border-[#e3dccb]"
      aria-labelledby="three-simple-steps-heading"
    >
      <div className="pointer-events-none absolute -top-28 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-[#2d5bff]/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 right-0 h-72 w-72 rounded-full bg-[#ff6b2c]/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20 lg:py-24">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-[#2d5bff] text-xs sm:text-sm uppercase tracking-[0.22em] font-semibold">
            3 simple steps
          </p>

          <h2
            id="three-simple-steps-heading"
            className="font-display text-3xl sm:text-4xl md:text-5xl text-[#0a1736] font-semibold leading-tight mt-4"
          >
            Find your next customers with a{" "}
            <span className="italic text-[#2d5bff]">visual workflow</span>.
          </h2>

          <p className="text-[#5a6478] mt-5 text-sm sm:text-base leading-relaxed max-w-2xl mx-auto">
            Build a campaign, define buying signals, and let Collavue surface the
            people already asking for what you sell.
          </p>
        </div>

        <div className="mt-12 sm:mt-16 mx-auto max-w-4xl">
          <div className="relative rounded-[28px] sm:rounded-[34px] border border-[#e3dccb] bg-[#faf6ee] p-4 sm:p-6 md:p-8 shadow-[0_24px_80px_-48px_rgba(10,23,54,0.45)]">
            <div className="absolute inset-0 rounded-[28px] sm:rounded-[34px] bg-[radial-gradient(circle_at_top,rgba(45,91,255,0.08),transparent_42%),radial-gradient(circle_at_bottom_right,rgba(255,107,44,0.08),transparent_38%)]" />

            <ol className="relative space-y-0">
              {steps.map((step, i) => {
                const isActive = i === active;
                const isComplete = active > i;

                return (
                  <li key={step.title} className="relative">
                    <motion.div
                      animate={{
                        y: isActive ? -3 : 0,
                        scale: isActive ? 1.015 : 1,
                        opacity: isActive ? 1 : 0.82,
                      }}
                      transition={{ duration: 0.35, ease: "easeOut" }}
                      className={`relative overflow-hidden rounded-3xl border bg-white p-5 sm:p-6 md:p-7 transition-all ${isActive
                        ? "border-[#2d5bff] shadow-[0_22px_60px_-34px_rgba(45,91,255,0.75)]"
                        : "border-[#e3dccb] shadow-sm"
                        }`}
                    >
                      <div
                        className={`pointer-events-none absolute inset-y-0 left-0 w-1.5 ${isActive || isComplete ? "bg-[#2d5bff]" : "bg-[#e3dccb]"
                          }`}
                      />

                      <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-start gap-4 sm:gap-5">
                          <motion.div
                            animate={{
                              scale: isActive ? [1, 1.08, 1] : 1,
                            }}
                            transition={{
                              duration: 1,
                              repeat: isActive ? Infinity : 0,
                            }}
                            className={`flex h-12 w-12 sm:h-14 sm:w-14 shrink-0 items-center justify-center rounded-2xl border font-display text-lg sm:text-xl font-semibold ${isActive || isComplete
                              ? "border-[#2d5bff] bg-[#eef3ff] text-[#2d5bff]"
                              : "border-[#e3dccb] bg-[#faf6ee] text-[#9a927f]"
                              }`}
                          >
                            0{i + 1}
                          </motion.div>

                          <div>
                            <p className="text-xs uppercase tracking-[0.18em] text-[#2d5bff] font-semibold">
                              {step.eyebrow}
                            </p>

                            <h3 className="font-display text-xl sm:text-2xl text-[#0a1736] font-semibold mt-2 leading-tight">
                              {step.title}
                            </h3>

                            <p className="text-[#5a6478] text-sm sm:text-base mt-2 leading-relaxed max-w-2xl">
                              {step.body}
                            </p>
                          </div>
                        </div>

                        <div
                          className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-semibold ${isActive
                            ? "bg-[#2d5bff] text-white"
                            : isComplete
                              ? "bg-[#eef3ff] text-[#2d5bff]"
                              : "bg-[#f1eadc] text-[#7a705d]"
                            }`}
                        >
                          {isActive ? "In progress" : isComplete ? "Complete" : "Next"}
                        </div>
                      </div>
                    </motion.div>

                    {i < steps.length - 1 && (
                      <div className="flex justify-center py-3 sm:py-4">
                        <motion.div
                          animate={{
                            y: isActive ? [0, 5, 0] : 0,
                            opacity: isActive || isComplete ? 1 : 0.45,
                          }}
                          transition={{
                            duration: 1.1,
                            repeat: isActive ? Infinity : 0,
                          }}
                          className={`relative flex h-12 w-12 items-center justify-center rounded-full border bg-white shadow-sm ${isActive || isComplete
                            ? "border-[#2d5bff] text-[#2d5bff]"
                            : "border-[#e3dccb] text-[#b6ad99]"
                            }`}
                          aria-hidden="true"
                        >
                          <span className="text-2xl leading-none">↓</span>
                        </motion.div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>

            <div className="relative mt-6 sm:mt-8 rounded-3xl border border-[#dbe4ff] bg-white/95 p-5 sm:p-6 shadow-sm">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <p className="text-xs uppercase tracking-[0.18em] text-[#2d5bff] font-semibold">
                    Example output
                  </p>

                  <h3 className="font-display text-xl sm:text-2xl text-[#0a1736] font-semibold mt-2">
                    Qualified lead found
                  </h3>

                  <p className="text-sm sm:text-base text-[#5a6478] mt-2 leading-relaxed">
                    A surfaced lead your team can review and act on.
                  </p>
                </div>

                <span className="w-fit rounded-full bg-[#eef3ff] px-3 py-1 text-xs font-semibold text-[#2d5bff]">
                  Intent score 91
                </span>
              </div>

              <motion.div
                animate={{
                  borderColor:
                    active === 2
                      ? "rgba(45,91,255,0.42)"
                      : "rgba(227,220,203,1)",
                  backgroundColor:
                    active === 2
                      ? "rgba(251,252,255,1)"
                      : "rgba(255,255,255,0.86)",
                }}
                transition={{ duration: 0.35 }}
                className="mt-5 rounded-2xl border p-4 sm:p-5"
              >
                <p className="text-sm sm:text-base font-semibold text-[#0a1736] leading-relaxed">
                  “Looking for a tool that helps us find high-intent leads from X
                  without manual searching.”
                </p>

                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="rounded-full bg-[#faf6ee] px-3 py-1 text-xs font-medium text-[#5a6478]">
                    Source: X
                  </span>
                  <span className="rounded-full bg-[#faf6ee] px-3 py-1 text-xs font-medium text-[#5a6478]">
                    Status: Ready to reply
                  </span>
                  <span className="rounded-full bg-[#faf6ee] px-3 py-1 text-xs font-medium text-[#5a6478]">
                    Buying signal detected
                  </span>
                </div>
              </motion.div>

              <AnimatePresence>
                {active === 2 && (
                  <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.35 }}
                    className="mt-5"
                  >
                    <p className="text-xs uppercase tracking-[0.18em] text-[#5a6478] mb-3 font-semibold">
                      Sources being scanned
                    </p>

                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                      {sources.map((source, i) => (
                        <motion.div
                          key={source}
                          animate={{
                            y: [0, -4, 0],
                            scale: [1, 1.03, 1],
                            opacity: [0.78, 1, 0.78],
                          }}
                          transition={{
                            duration: 1.6,
                            repeat: Infinity,
                            delay: i * 0.15,
                          }}
                          className="rounded-2xl border border-[#d8deea] bg-white px-4 py-3 text-sm font-semibold text-[#0a1736] text-center shadow-sm"
                        >
                          {source}
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
function FlowNode({ label, subtitle, number, isActive, isComplete }) {
  return (
    <motion.div
      animate={{
        y: isActive ? -4 : 0,
        scale: isActive ? 1.02 : 1,
        opacity: isActive || isComplete ? 1 : 0.78,
      }}
      transition={{ duration: 0.35 }}
      className={`relative rounded-2xl border px-4 py-5 bg-white shadow-sm min-h-[120px] ${isActive
        ? "border-[#2d5bff] shadow-[0_14px_35px_-24px_rgba(45,91,255,0.45)]"
        : isComplete
          ? "border-[#cdd9ff]"
          : "border-[#e3dccb]"
        }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold tracking-wider text-[#2d5bff]">
          {number}
        </span>

        <motion.span
          animate={{
            scale: isActive ? [1, 1.15, 1] : 1,
            opacity: isActive ? [0.7, 1, 0.7] : 1,
          }}
          transition={{
            duration: 1,
            repeat: isActive ? Infinity : 0,
          }}
          className={`h-2.5 w-2.5 rounded-full ${isActive
            ? "bg-[#2d5bff]"
            : isComplete
              ? "bg-[#88a6ff]"
              : "bg-[#c7bfae]"
            }`}
        />
      </div>

      <h3 className="font-display text-lg text-[#0a1736] font-semibold mt-3">
        {label}
      </h3>
      <p className="text-sm text-[#5a6478] mt-2 leading-relaxed">{subtitle}</p>
    </motion.div>
  );
}

function LeadNode({ active }) {
  return (
    <motion.div
      animate={{
        y: active ? -4 : 0,
        scale: active ? 1.02 : 1,
        opacity: active ? 1 : 0.85,
      }}
      transition={{ duration: 0.35 }}
      className={`rounded-2xl border px-4 py-5 bg-[#f7faff] min-h-[120px] ${active ? "border-[#2d5bff]" : "border-[#d8deea]"
        }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold tracking-wider text-[#2d5bff]">
          OUTPUT
        </span>

        <motion.span
          animate={{
            scale: active ? [1, 1.15, 1] : 1,
            opacity: active ? [0.7, 1, 0.7] : 1,
          }}
          transition={{
            duration: 1,
            repeat: active ? Infinity : 0,
          }}
          className={`h-2.5 w-2.5 rounded-full ${active ? "bg-[#2d5bff]" : "bg-[#b6c3e1]"
            }`}
        />
      </div>

      <h3 className="font-display text-lg text-[#0a1736] font-semibold mt-3">
        Qualified Leads
      </h3>
      <p className="text-sm text-[#5a6478] mt-2 leading-relaxed">
        Ranked by intent so your team focuses only on the best opportunities.
      </p>
    </motion.div>
  );
}

function FlowConnector({ active }) {
  return (
    <div className="hidden md:flex items-center justify-center">
      <div className="relative h-1 w-14 rounded-full bg-[#e4e8f0] overflow-hidden">
        <motion.div
          initial={false}
          animate={{ width: active ? "100%" : "0%" }}
          transition={{ duration: 0.45 }}
          className="absolute left-0 top-0 h-full bg-[#2d5bff]"
        />

        {active && (
          <motion.div
            initial={false}
            animate={{ x: [0, 40, 0] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-1/2 h-3 w-3 -translate-y-1/2 rounded-full bg-[#2d5bff] shadow-[0_0_0_4px_rgba(45,91,255,0.14)]"
          />
        )}
      </div>
    </div>
  );
}


function InterestSection() {
  return (
    <section className="bg-[#faf6ee] border-t border-[#e3dccb]">
      <div className="max-w-5xl mx-auto px-6 py-20 text-center">
        <h2 className="font-display text-4xl md:text-5xl text-[#0a1736] font-semibold leading-tight">
          Talk Only to People Who Showed Interest
        </h2>
        <p className="mt-5 text-lg text-[#5a6478] leading-relaxed">
          Forget burning money on ads — Collavue connects you directly with people
          actively looking for your product on X (Twitter), LinkedIn, and Reddit, at a
          fraction of the cost of paid acquisition.
        </p>
      </div>
    </section>
  );
}

export const LANDING_FAQS = [
  {
    q: "How do I find leads on X (Twitter), LinkedIn, and Reddit?",
    a: "Search for buying intent rather than for your product category: phrases like \"anyone recommend\", \"looking for a\" or \"switching from\" combined with your category surface people who are actively shopping. Collavue runs those searches continuously across X, LinkedIn, and Reddit, scores every match on intent and ICP fit, and drafts the reply for you to approve.",
  },
  {
    q: "How is this different from paid ads?",
    a: "Ads interrupt people who were not thinking about you. Collavue reaches people who have already described the problem you solve, in public, in the last few hours — so you start the conversation with context instead of a banner.",
  },
  {
    q: "Do you auto-send replies without approval?",
    a: "No. Every lead and every drafted reply waits in your queue until a human approves it. Nothing posts or sends on its own, which keeps you inside both platforms' rules on automated and inauthentic engagement.",
  },
  {
    q: "What platforms do you scan?",
    a: "Collavue scans X (Twitter), LinkedIn, and Reddit — where B2B buyers vent about their tools, ask for recommendations, and post about the problems they are solving. More channels are on the roadmap.",
  },
  {
    q: "How quickly will I see leads?",
    a: "Most workspaces see their first scored leads within 24 hours of describing their offer, because the conversations are already happening — the campaign just has to know what to look for.",
  },
  {
    q: "Is this safe for my account?",
    a: "Yes. Collavue never scrapes behind a login or automates sending. Discovery, scoring and drafting are automated; the send button stays with you, which is the pattern both platforms permit.",
  },
];

function FAQSection() {
  const faqs = LANDING_FAQS;

  return (
    <section
      id="faq"
      className="bg-white border-t border-[#e3dccb]"
      aria-labelledby="faq-heading"
    >
      <div className="max-w-4xl mx-auto px-6 py-20">
        <h2
          id="faq-heading"
          className="font-display text-3xl md:text-4xl text-[#0a1736] font-semibold"
        >
          Frequently asked questions
        </h2>
        <div className="mt-8 space-y-4">
          {faqs.map((item) => (
            <div key={item.q} className="rounded-xl border border-[#e3dccb] p-5 bg-[#faf6ee]/40">
              <h3 className="font-semibold text-[#0a1736]">{item.q}</h3>
              <p className="mt-2 text-[#5a6478] text-sm leading-relaxed">{item.a}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// Internal links into the guides cluster. The homepage is the strongest page on
// the domain, so the pillar guides get their descriptive anchor text from here.
function GuidesSection() {
  const guides = [
    {
      to: "/twitter-lead-generation",
      title: "How to find leads on X (Twitter)",
      body: "Advanced search operators that surface buying intent, competitor complaint mining, and the reply-first sequence that gets answers.",
    },
    {
      to: "/linkedin-lead-generation",
      title: "How to find leads on LinkedIn",
      body: "Boolean searches on the Posts tab, Sales Navigator Spotlights, hiring signals, and a warm outreach sequence that stays inside the limits.",
    },
    {
      to: "/reddit-lead-generation",
      title: "How to find leads on Reddit",
      body: "The search operators that scope intent to one subreddit, the self-promotion rules that get marketers removed, and the comment-first sequence that gets upvoted instead.",
    },
    {
      to: "/lead-generation",
      title: "X (Twitter) vs LinkedIn vs Reddit for lead generation",
      body: "Which channel to start with, how the three differ on intent, precision and shelf life, and the fundamentals that apply to all of them.",
    },
  ];

  return (
    <section
      id="guides"
      className="bg-white border-t border-[#e3dccb]"
      aria-labelledby="guides-heading"
    >
      <div className="max-w-7xl mx-auto px-6 py-20">
        <p className="text-[#2d5bff] text-sm uppercase tracking-wider font-medium">
          Free playbooks
        </p>
        <h2
          id="guides-heading"
          className="font-display text-3xl md:text-4xl text-[#0a1736] font-semibold mt-3 leading-tight"
        >
          Learn the manual version first.
        </h2>
        <p className="mt-4 text-[#5a6478] max-w-3xl leading-relaxed">
          Everything Collavue automates, you can do by hand — and understanding
          how it works makes the automated version far more effective. These are
          the full playbooks, no signup required.
        </p>

        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {guides.map((g) => (
            <Link
              key={g.to}
              to={g.to}
              className="group rounded-xl border border-[#e3dccb] bg-[#faf6ee]/50 p-6 hover:border-[#2d5bff] hover:shadow-[0_10px_40px_-25px_rgba(45,91,255,0.45)] transition-all"
            >
              <h3 className="font-display text-xl text-[#0a1736] font-semibold leading-snug">
                {g.title}
              </h3>
              <p className="mt-3 text-sm text-[#5a6478] leading-relaxed">{g.body}</p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#0a1736] group-hover:text-[#2d5bff] transition-colors">
                Read the playbook
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="bg-[#faf6ee] border-t border-[#e3dccb]">
      <div className="max-w-5xl mx-auto px-6 py-24 text-center">
        <Shield className="w-8 h-8 text-[#2d5bff] mx-auto" />
        <h2 className="font-display text-4xl md:text-5xl text-[#0a1736] font-semibold mt-4 leading-tight">
          Stop losing leads to{" "}
          <span className="italic text-[#2d5bff]">vendor silence.</span>
        </h2>
        <p className="mt-4 text-[#5a6478] text-lg max-w-2xl mx-auto">
          Your future customers are complaining about your competitors right now.
          Collavue finds them, drafts the reply, and books the demo.
        </p>
        <div className="mt-8 flex flex-wrap gap-8 justify-center">
          <Link
            to="/campaigns/new"
            className="px-6 py-3 rounded-md bg-[#0a1736] text-white font-medium hover:bg-[#1c2a4d] transition-colors"
          >
            Sign Up
          </Link>
          <a
            href="#book-demo"
            className="px-6 py-3 rounded-md bg-white text-[#0a1736] border border-[#e3dccb] font-medium hover:border-[#0a1736] transition-colors"
          >
            Book a Demo
          </a>
        </div>
      </div>
    </section>
  );
}

export default function Landing() {
  // One graph per entity rather than a single blob: the product, the company,
  // the site, the homepage FAQ and the guides cluster are separate things, and
  // splitting them is what lets each one be understood (and cited) on its own.
  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "Collavue",
    applicationCategory: "BusinessApplication",
    applicationSubCategory: "Lead generation software",
    operatingSystem: "Web",
    url: SITE_URL,
    description:
      "AI lead discovery and reply agent. Find people on X (Twitter), LinkedIn, and Reddit asking for what you offer — score their intent, and reply in your brand voice with human approval.",
    featureList: [
      "X (Twitter), LinkedIn, and Reddit lead discovery",
      "Intent and ICP-fit scoring, 0-100",
      "Brand-voice reply drafting",
      "Human-in-the-loop approval queue",
      "Unified lead inbox with outcome tracking",
    ],
    offers: {
      "@type": "Offer",
      price: "5",
      priceCurrency: "USD",
      url: `${SITE_URL}/pricing`,
      availability: "https://schema.org/OnlineOnly",
    },
    publisher: ORGANIZATION_SCHEMA,
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    ...ORGANIZATION_SCHEMA,
    description:
      "Collavue is an AI lead discovery and reply platform for B2B teams selling on X (Twitter), LinkedIn, and Reddit.",
  };

  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: "Collavue",
    url: SITE_URL,
    inLanguage: "en-US",
    publisher: ORGANIZATION_SCHEMA,
  };

  return (
    <div className="bg-[#faf6ee] text-[#0a1736] -mt-px">
      <SEO
        title="Collavue — Find B2B Leads on X, LinkedIn & Reddit with AI"
        description="Collavue finds people on X (Twitter), LinkedIn, and Reddit asking for what you sell, scores their intent, and drafts on-brand replies for your approval. Start free."
        path="/"
        keywords="find leads on twitter, find leads on linkedin, AI lead generation, X lead generation, twitter lead generation tool, linkedin lead generation software, social selling, buying intent signals, AI reply agent, B2B social outreach, GummySearch alternative, Devi AI alternative"
        imageAlt="Collavue — AI lead discovery for X (Twitter), LinkedIn, and Reddit"
        schema={[
          softwareSchema,
          organizationSchema,
          websiteSchema,
          faqSchema(LANDING_FAQS),
        ]}
      />
      <MarketingNav />
      <Hero />
      <CaseStudyAnimation />
      <ReplyAgentSection />
      <HowItWorks />
      <ThreeSimpleSteps />
      <InterestSection />
      <GuidesSection />
      <BookDemo />
      <FAQSection />
      <FinalCTA />
      <MarketingFooter />
    </div>
  );
}
